/**
 * Image Component
 */

import React, { useEffect, useRef, useState } from 'react';
import { ImageProps } from './image.types';
import { ImageWrapper, Img, Placeholder } from './image.style';
import LazyLoad from 'react-lazyload';
import FixedAspect from '../utilComponents/FixedAspect';
import bindHandlers from '../../utils/handlerHelper';
import useDidUpdate from '../hooks/useDidUpdate';
import Handler from '../../commonTypes/handler.types';
import { SkeletonColors } from '../Skeleton/skeleton.types';

// Construct a srcset string if passed an object
export const getSrcsetString = (srcset: object | string): string => {
  if (typeof srcset === 'string') {
    return srcset;
  }
  const arr = [];
  for (const s in srcset) {
    if (Object.prototype.hasOwnProperty.call(srcset, s)) {
      arr.push(`${srcset[s as keyof object]} ${s}`);
    }
  }
  return arr.join(', ');
};

// Made the Image component a class to make use of state for
// error image handing
const Image = ({
  lazyload = false,
  srcset,
  sizes,
  src,
  rounded = false,
  width = '100%',
  height,
  alt,
  title,
  lazyloadOffset = 200,
  placeHolderHeight,
  placeHolderWidth,
  errorImageSrc,
  fixAspect = false,
  ratio,
  handlers = {},
  backgroundColor,
  className,
  border,
  padding = '0px',
  borderRadius,
  theme = {},
  dataAttr,
}: ImageProps) => {
  const [visible, setVisible] = useState<boolean>(true);
  const [srcSetDisabled, setSrcSetDisabled] = useState<boolean>(false);
  const [errSrc, setErrorImageSrc] = useState<string | undefined>(undefined);
  const imgRef = useRef<HTMLImageElement>(null);

  useDidUpdate(() => {
    // Reset error states if img src changes.
    // Needed if a src changes from error src to new src.
    // Purposely not resetting if srcset changes since that can be an un-memoized object.
    setSrcSetDisabled(false);
    setErrorImageSrc(undefined);
    setVisible(true);
  }, [src]);

  // Handle the image error.
  // If there is a handler, call it.
  // If there is a provided error image src, use it
  // Else if we have a fallback image, use it
  // If nothing else, set to invisible
  const handleImageError = () => {
    if (errorImageSrc) {
      setSrcSetDisabled(true);
      setErrorImageSrc(errorImageSrc);
    } else {
      setVisible(false);
    }
  };

  useEffect(() => {
    // Since our needs include SSR.
    // The img may load or error out before JS has parsed, in these cases onLoad or onError event will not fire.
    // On mount we check if the img has loaded, if not return and binded handlers will take care of everything.
    if (!imgRef.current?.complete) {
      return;
    }
    // If the complete image has no width, it's an indication of a possible error
    // FF returns has a known bug where it returns 0 on SVG files
    // Because of this we can reset the src and have the browser trigger load/error
    if (imgRef.current.naturalWidth === 0) {
      // eslint-disable-next-line no-self-assign
      imgRef.current.src = imgRef.current.src;
      return;
    }
    // If our image has loaded, let's trigger the onLoad handler if applicable
    if (handlers?.onLoad) {
      handlers.onLoad();
    }
  }, []);

  // Establish the src and srcset strings
  const { dark = false } = theme;

  const srcString: string = errSrc || src;
  const srcsetString: string | undefined = srcset && !srcSetDisabled ? getSrcsetString(srcset) : undefined;

  // Set up the handlers
  const imageHandlers: Handler = {
    onError: () => {
      handleImageError();
    },
  };
  const combinedHandlers: Handler = bindHandlers(null, handlers, imageHandlers);

  // Set up the image itself
  let image = (
    <Img
      ref={imgRef}
      key={src}
      srcSet={srcsetString}
      sizes={sizes}
      src={srcString}
      alt={alt}
      title={title}
      width={fixAspect ? '100%' : width}
      height={height}
      backgroundColor={backgroundColor}
      padding={padding}
      rounded={rounded}
      borderRadius={borderRadius}
      {...combinedHandlers}
      {...dataAttr}
    />
  );

  // Fix the image aspect ratio if flag is passed
  if (fixAspect) {
    image = <FixedAspect ratio={ratio}>{image}</FixedAspect>;
  }

  if (lazyload) {
    const placeholderBgColor = dark ? SkeletonColors.dark.main : SkeletonColors.primary.main;
    const placeholderComp = (
      <Placeholder
        h={height}
        w={width}
        placeholderWidth={placeHolderWidth}
        placeholderHeight={placeHolderHeight}
        backgroundColor={placeholderBgColor}
        border={border}
        ratio={ratio}
      />
    );

    image = (
      <LazyLoad placeholder={placeholderComp} once offset={[lazyloadOffset, 0]}>
        {image}
      </LazyLoad>
    );
  }

  return (
    <ImageWrapper
      className={className}
      rounded={rounded}
      visible={visible}
      w={width}
      h={height}
      border={border}
      borderRadius={borderRadius}
    >
      {image}
    </ImageWrapper>
  );
};

export default Image;
